import { Link, matchPath, useLocation, useNavigate } from "react-router-dom"
import { useState, useEffect, useMemo } from "react"
import { cn } from "../lib/utils"
import { useAuth } from "@/contexts/AuthContext"
import {
  LayoutDashboard,
  Users,
  Building2,
  Calendar,
  Settings,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  Sun,
  Moon,
  LogOut,
  FileText,
  Tag,
  Image,
  BatteryIcon,
  MessageSquare,
  BarChart3,
  Wallet,
  Banknote,
  Receipt,
  Gift,
  Ban,
  UserX,
  TrendingUp,
  ShieldCheck,
  Globe,
  GraduationCap,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { useTheme } from "./theme-provider"
import { useInfluencerDetail } from "@/hooks/influencer/useInfluencerDetail"
import { ProfileStatus } from "@/models"

type NavItem = {
  name: string
  href: string
  icon: LucideIcon
  badge?: string | null
}

type NavGroup = {
  type: "group"
  name: string
  icon: LucideIcon
  children: NavItem[]
}

type NavLink = NavItem & { type?: "link" }

type NavEntry = NavLink | NavGroup

const navigation: NavEntry[] = [
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard, badge: null },
  {
    type: "group",
    name: "Influencers",
    icon: Users,
    children: [
      { name: "Active Influencers", href: "/influencers", icon: Users },
      { name: "Blocked Influencers", href: "/influencers/blocked", icon: Ban },
      { name: "Rejected Influencers", href: "/influencers/rejected", icon: UserX },
      { name: "Influencer Contacts", href: "/influencer-contacts", icon: MessageSquare },
      { name: "Influencer Statistics", href: "/influencer-statistics", icon: BarChart3 },
    ],
  },
  { name: "Organizations", href: "/organizations", icon: Building2 },
  { name: "Leads", href: "/leads", icon: TrendingUp },
  { name: "Events", href: "/events", icon: Calendar },
  { name: "Influencer Sign-Up Requests", href: "/signup-requests", icon: FileText },
  { name: "Organization Sign-Up Requests", href: "/signup-requests/organizations", icon: Building2 },
  { name: "Event Requests", href: "/event-requests", icon: Calendar },
  {
    type: "group",
    name: "Finance",
    icon: Wallet,
    children: [
      { name: "Rewards", href: "/rewards", icon: Gift },
      { name: "Withdrawal Requests", href: "/withdrawals", icon: Banknote },
      { name: "Legacy Withdrawals", href: "/finance/withdrawals", icon: Banknote },
      { name: "Transactions", href: "/finance/transactions", icon: Receipt },
    ],
  },
  { name: "Categories", href: "/categories", icon: Tag },
  { name: "Banners", href: "/banners", icon: Image },
  { name: "Academy", href: "/academy", icon: GraduationCap },
  { name: "Countries", href: "/countries", icon: Globe },
  { name: "Sign Up Attempts", href: "/signup-attempts", icon: BatteryIcon },
  { name: "Admins", href: "/admins", icon: ShieldCheck },
  { name: "Settings", href: "/settings", icon: Settings },
]

interface SidebarProps {
  onCollapsedChange?: (collapsed: boolean) => void
}

const groupOpenStorageKey = (name: string) => `sidebar-group-open-${name.toLowerCase()}`
const ADMIN_MANAGEMENT_USERS = new Set(["mamur", "dinara"])

export function Sidebar({ onCollapsedChange }: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { theme, setTheme } = useTheme()
  const { logout, getUsername } = useAuth()
  // Influencer detail pages (/influencers/:id, /edit, /wallet) belong to the Active, Blocked or
  // Rejected menu item depending on the influencer's status. Same query key as the detail page,
  // so this shares its cache instead of adding a request.
  const detailMatch = matchPath({ path: "/influencers/:id/*", end: false }, location.pathname)
  const detailInfluencerId = /^\d+$/.test(detailMatch?.params.id ?? "") ? Number(detailMatch?.params.id) : 0
  const { influencerInfo: detailInfluencerInfo } = useInfluencerDetail(detailInfluencerId)
  const detailProfileStatus = detailInfluencerInfo?.influencer.profileStatus
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})

  const username = getUsername() || "Admin User"
  const canManageAdmins = ADMIN_MANAGEMENT_USERS.has(username.trim().toLowerCase())
  const visibleNavigation = useMemo(() => navigation.filter((entry) => {
    if ((entry as NavItem).href === "/admins") return canManageAdmins
    return true
  }), [canManageAdmins])
  const userInitial = username.charAt(0).toUpperCase()

  useEffect(() => {
    const saved = localStorage.getItem("sidebar-collapsed")
    if (saved !== null) {
      const collapsed = JSON.parse(saved)
      setIsCollapsed(collapsed)
      onCollapsedChange?.(collapsed)
    }
  }, [onCollapsedChange])

  useEffect(() => {
    const initial: Record<string, boolean> = {}
    visibleNavigation.forEach((entry) => {
      if ((entry as NavGroup).type === "group") {
        const group = entry as NavGroup
        const stored = localStorage.getItem(groupOpenStorageKey(group.name))
        const groupActive = group.children.some((child) => location.pathname.startsWith(child.href))
        initial[group.name] = stored !== null ? stored === "true" : groupActive
      }
    })
    setOpenGroups(initial)
  }, [location.pathname, visibleNavigation])

  const toggleCollapsed = () => {
    const newCollapsed = !isCollapsed
    setIsCollapsed(newCollapsed)
    localStorage.setItem("sidebar-collapsed", JSON.stringify(newCollapsed))
    onCollapsedChange?.(newCollapsed)
  }

  const toggleGroup = (groupName: string) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [groupName]: !prev[groupName] }
      localStorage.setItem(groupOpenStorageKey(groupName), String(next[groupName]))
      return next
    })
  }

  const handleLogout = () => {
    logout()
    navigate("/login")
  }

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark")
  }

  const isLinkActive = (href: string) => {
    if (href === "/influencers" || href === "/influencers/blocked" || href === "/influencers/rejected") {
      if (location.pathname === href) return true
      if (detailInfluencerId === 0) return false
      // Detail page: highlight the list the influencer belongs to; none while the status is loading
      if (!detailProfileStatus) return false
      if (detailProfileStatus === ProfileStatus.BLOCKED) return href === "/influencers/blocked"
      if (detailProfileStatus === ProfileStatus.REJECTED) return href === "/influencers/rejected"
      return href === "/influencers"
    }
    if (href === "/signup-requests") {
      // influencer item: sibling org pages (/organizations, /organization/:id) must not match
      const p = location.pathname
      if (p === "/signup-requests/organizations" || p.startsWith("/signup-requests/organization/")) return false
    }
    if (href === "/signup-attempts") {
      // exact + detail (journey) only — siblings like /signup-attempts/map must not match
      return location.pathname === href || location.pathname === "/signup-attempts/journey"
    }
    return location.pathname === href || location.pathname.startsWith(`${href}/`)
  }

  const renderLink = (item: NavItem, depth: number) => {
    const active = isLinkActive(item.href)
    return (
      <Link
        key={item.name}
        to={item.href}
        className={cn(
          "group flex items-center justify-between text-sm font-medium rounded-xl transition-all duration-200 hover:scale-[1.02]",
          isCollapsed ? "px-3 py-3" : "px-4 py-3",
          depth > 0 && !isCollapsed && "ml-4 pl-3",
          active
            ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-600/25"
            : "text-muted-foreground hover:text-foreground hover:bg-accent/50 hover:shadow-sm",
        )}
        title={isCollapsed ? item.name : undefined}
      >
        <div className={cn("flex items-center", isCollapsed && "justify-center w-full")}>
          <item.icon
            className={cn(
              "h-5 w-5 transition-transform duration-200",
              active ? "text-white" : "text-muted-foreground group-hover:text-foreground",
              isCollapsed ? "mr-0" : "mr-4",
            )}
          />
          {!isCollapsed && <span className="font-medium">{item.name}</span>}
        </div>
        {!isCollapsed && (
          <div className="flex items-center space-x-2">
            {item.badge && (
              <Badge
                variant={active ? "secondary" : "outline"}
                className={cn(
                  "text-xs font-semibold",
                  active ? "bg-white/20 text-white border-white/30" : "bg-background",
                )}
              >
                {item.badge}
              </Badge>
            )}
            {active && <ChevronRight className="h-4 w-4 text-white/70" />}
          </div>
        )}
      </Link>
    )
  }

  const renderGroup = (group: NavGroup) => {
    const groupActive = group.children.some((child) => isLinkActive(child.href))
    const open = openGroups[group.name] ?? false

    if (isCollapsed) {
      return group.children.map((child) => renderLink(child, 0))
    }

    return (
      <div key={group.name} className="space-y-1">
        <button
          type="button"
          onClick={() => toggleGroup(group.name)}
          className={cn(
            "w-full group flex items-center justify-between text-sm font-medium rounded-xl transition-all duration-200 hover:scale-[1.02]",
            "px-4 py-3",
            groupActive
              ? "text-foreground bg-accent/40"
              : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
          )}
        >
          <div className="flex items-center">
            <group.icon className="h-5 w-5 mr-4 text-muted-foreground group-hover:text-foreground"/>
            <span className="font-medium">{group.name}</span>
          </div>
          {open ? <ChevronDown className="h-4 w-4"/> : <ChevronRight className="h-4 w-4"/>}
        </button>
        {open && (
          <div className="space-y-1">
            {group.children.map((child) => renderLink(child, 1))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        "h-screen bg-gradient-to-b from-card to-card/50 border-r border-border/50 shadow-sm transition-all duration-300 ease-in-out flex flex-col",
        isCollapsed ? "w-20" : "w-72",
      )}
    >
      <div className={cn("flex-shrink-0", isCollapsed ? "p-2" : "p-6")}>
        {isCollapsed ? (
          <div className="flex flex-col items-center space-y-3">
            <div className="flex flex-col space-y-2">
              <Button variant="ghost" size="sm" onClick={toggleCollapsed} className="h-8 w-8 p-0 hover:bg-accent/50">
                <Menu className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="h-8 w-8 p-0 hover:bg-accent/50"
                title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-lg">B</span>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="h-8 w-8 p-0 hover:bg-accent/50"
                title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="sm" onClick={toggleCollapsed} className="h-8 w-8 p-0 hover:bg-accent/50">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg">B</span>
              </div>
              <div>
                <h1 className="font-bold text-xl text-foreground tracking-tight">BETWEEN</h1>
                <p className="text-sm text-muted-foreground font-medium">EST.2024</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <nav className={cn("flex-1 overflow-y-auto space-y-2", isCollapsed ? "px-2 mt-4" : "px-6")}>
        {visibleNavigation.map((entry) => {
          if ((entry as NavGroup).type === "group") {
            return renderGroup(entry as NavGroup)
          }
          return renderLink(entry as NavItem, 0)
        })}
      </nav>

      <div className={cn("flex-shrink-0 p-6", isCollapsed && "p-2")}>
        <div className={cn("bg-accent/30 rounded-xl border border-border/50", isCollapsed ? "p-2" : "p-4")}>
          <div className={cn("flex items-center", isCollapsed ? "flex-col space-y-2" : "space-x-3")}>
            <div
              className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0"
              title={isCollapsed ? username : undefined}
            >
              <span className="text-white font-semibold text-sm">{userInitial}</span>
            </div>
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{username}</p>
                <p className="text-xs text-muted-foreground truncate">Admin</p>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className={cn(
                "hover:bg-red-500/10 hover:text-red-500 transition-colors",
                isCollapsed ? "h-8 w-8 p-0" : "h-8 px-3",
              )}
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
              {!isCollapsed && <span className="ml-2 text-xs">Logout</span>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
